"use client";

/**
 * App Router pageview tracker.
 *
 * Next.js App Router does not fire route-change events the way the Pages
 * router did, so PostHog cannot auto-capture pageviews reliably. We watch
 * `usePathname()` + `useSearchParams()` and fire `$pageview` on every
 * navigation.
 *
 * Mount this ONCE, high in the tree (RootLayout is ideal).
 */

import { useEffect, useRef, Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { getPosthog } from "./posthog-client";
import { track } from "./track";

function PageviewTrackerInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastUrl = useRef(null);

  useEffect(() => {
    if (!pathname) return;

    const search = searchParams?.toString();
    const url = search ? `${pathname}?${search}` : pathname;

    // Deduplicate rapid re-renders that produce the same URL
    if (url === lastUrl.current) return;
    lastUrl.current = url;

    const ph = getPosthog();
    if (ph) {
      // Use PostHog's built-in $pageview so heatmaps/session recordings
      // stay in sync. Also pass the full URL for accurate reporting.
      ph.capture("$pageview", {
        $current_url: window.location.origin + url,
        $pathname: pathname,
      });
    } else {
      // Client not initialized yet — buffer via track() which will init.
      track("$pageview", {
        $current_url:
          typeof window !== "undefined" ? window.location.origin + url : url,
        $pathname: pathname,
      });
    }
  }, [pathname, searchParams]);

  return null;
}

export default function PageviewTracker() {
  // useSearchParams needs a Suspense boundary in App Router.
  return (
    <Suspense fallback={null}>
      <PageviewTrackerInner />
    </Suspense>
  );
}
